// Müşteri portalı KPI — /api/v1/portal/kpi/* typed wrapper'ları (M16 T19; route tablosu:
// routes/portal-kpi.ts + portal-kpi-import.ts + portal-kpi-evidence.ts). Cookie auth; set erişimi
// yoksa 403 KPI_CLIENT_FORBIDDEN (asla PARTICIPATION_ENDED — RK22, kickout tetiklemez). Yükleme
// `uploadFileWithProgress` (ApiError döner); yazma 409'u `putPortalValueRaw` ile ayrıştırılır
// (müşteri stale-write'ta yalnız "sunucudaki değeri göster" — DR8).

import type {
  EvidenceDocument,
  KpiAccessRole,
  KpiCategoryAccessMap,
  KpiEvidenceLocator,
  KpiResolvedGrid,
  KpiSet,
  KpiSetIndicator,
  KpiTotalKind,
  KpiValueUpsert,
  SetFillStatus,
} from "@mahir/shared";
import { ApiError, api, apiRaw, uploadFileWithProgress } from "./client";
import type { KpiValueDto } from "./kpi";
import type { KpiLinkedEvidence } from "./kpi-evidence";
import { downloadKpiXlsx } from "./kpi-export";
import {
  KpiImportRequestError,
  defaultsBody,
  kpiImportMime,
  type ImportApi,
  type KpiImportCommitResult,
  type KpiImportJobDto,
  type KpiImportPreview,
  type ZodIssueLike,
} from "./kpi-import";

export interface PortalAccessSummary {
  open: number;
  readonly: number;
  closed: number;
}

export interface PortalSetSummary extends KpiSet {
  clientName: string;
  indicatorCount: number;
  access: PortalAccessSummary;
  /** v2 kategori haritası (`open`/`readonly`); NULL = tam erişim. */
  categoriesAllowed: KpiCategoryAccessMap | null;
  /** İşletme kapsamı (yaprak id'leri); NULL = tüm işletmeler. */
  entitiesAllowed: string[] | null;
  overridesCount: number;
  fullEntityScope: boolean;
  /** Toplam sütunu türü — etiket `KPI_TOTAL_KIND_LABELS`'tan (Y6). */
  totalKind: KpiTotalKind;
  /** M17 — set erişim rolü; `auditor` yalnız görüntüler (portal denetçi bandı). */
  role: KpiAccessRole;
  grantedAt: string;
  /** TODO-KPI-16 — sorumlu danışman (Özet "Onay durumu" paneli); sunucu bulamazsa null. */
  consultant: { fullName: string; email: string } | null;
}

export interface PortalResolvedResponse {
  grid: KpiResolvedGrid;
  cycle: string[] | null;
  fill: SetFillStatus;
  access: PortalAccessSummary;
}

// ─── Setler / tablo ─────────────────────────────────────────────────────────
export async function listPortalKpiSets(): Promise<PortalSetSummary[]> {
  const res = await api<{ sets: PortalSetSummary[] }>("/portal/kpi/sets");
  return res.sets;
}

export async function getPortalKpiSet(setId: string): Promise<{
  set: KpiSet;
  /** v2 Y4a — `structural: true` satırlar yalnız iskelet (kapalı kategori, görünür çocuklu). */
  indicators: (KpiSetIndicator & { structural?: true })[];
  access: PortalAccessSummary;
  categoriesAllowed: KpiCategoryAccessMap | null;
  entitiesAllowed: string[] | null;
  overridesCount: number;
  role: KpiAccessRole;
}> {
  return api(`/portal/kpi/sets/${setId}`);
}

export async function getPortalKpiResolved(
  setId: string,
  opts: { years: number[]; entityIds?: string[] },
): Promise<PortalResolvedResponse> {
  const q = new URLSearchParams();
  if (opts.years.length > 0) q.set("years", opts.years.join(","));
  if (opts.entityIds && opts.entityIds.length > 0) q.set("entityIds", opts.entityIds.join(","));
  return api(`/portal/kpi/sets/${setId}/resolved?${q.toString()}`);
}

export type PortalPutResult =
  | { ok: true; value: KpiValueDto }
  | { ok: false; stale: true; current: KpiValueDto | null; message: string }
  /** D20/DR9 — 409 KPI_UNIT_CHANGED: yazılmadı; `current` = güncel etkin birim */
  | { ok: false; unitChanged: true; current: string | null; message: string }
  | { ok: false; stale: false; status: number; code: string; message: string };

/**
 * PUT /portal/kpi/values — 409 KPI_STALE_WRITE `details.current` (değer) ile, 409 KPI_UNIT_CHANGED
 * `details.current` (güncel birim) ile döner; ikisinde de yazım yok (üzerine yazma YOK).
 */
export async function putPortalValueRaw(
  body: { setId: string } & KpiValueUpsert,
): Promise<PortalPutResult> {
  const res = await apiRaw<{
    value?: KpiValueDto;
    error?: string;
    message?: string;
    details?: { current?: KpiValueDto | string | null } | null;
  }>("/portal/kpi/values", { method: "PUT", body });
  if (res.ok && res.body.value) return { ok: true, value: res.body.value };
  const code = res.body?.error ?? "unknown";
  const message = res.body?.message ?? `HTTP ${res.status.toString()}`;
  if (res.status === 409 && code === "KPI_STALE_WRITE") {
    const current = res.body.details?.current;
    return {
      ok: false,
      stale: true,
      current: current && typeof current === "object" ? current : null,
      message,
    };
  }
  if (res.status === 409 && code === "KPI_UNIT_CHANGED") {
    const current = res.body.details?.current;
    return {
      ok: false,
      unitChanged: true,
      current: typeof current === "string" ? current : null,
      message,
    };
  }
  return { ok: false, stale: false, status: res.status, code, message };
}

/**
 * TODO-KPI-10 — şirket yöneticisi `submitted` değeri şirket adına onaylar (`client_approved`);
 * danışman onayı hâlâ gerekir. 403 (yönetici değil) / 400 (durum uygun değil) / 409 (sürüm).
 */
export async function clientApprovePortalValue(
  valueId: string,
  setId: string,
  expectedVersion: number,
): Promise<KpiValueDto> {
  const res = await api<{ value: KpiValueDto }>(`/portal/kpi/values/${valueId}/client-approve`, {
    method: "POST",
    body: { setId, expectedVersion },
  });
  return res.value;
}

export async function downloadPortalSetExcel(setId: string, years: number[] = []): Promise<void> {
  const q = years.length > 0 ? `?years=${years.join(",")}` : "";
  await downloadKpiXlsx(`/portal/kpi/sets/${setId}/export.xlsx${q}`, "kpi.xlsx");
}

// ─── Kanıt (portal presign → PUT /portal/kpi/evidence/:id/upload → commit) ──
export async function presignPortalEvidence(body: {
  setId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}): Promise<{ evidence: EvidenceDocument; uploadUrl: string; expiresIn: number }> {
  return api("/portal/kpi/evidence/presign", { method: "POST", body });
}

export async function commitPortalEvidence(evidenceId: string): Promise<EvidenceDocument> {
  const res = await api<{ evidence: EvidenceDocument }>(
    `/portal/kpi/evidence/${evidenceId}/commit`,
    { method: "POST" },
  );
  return res.evidence;
}

export async function uploadPortalEvidence(
  setId: string,
  file: File,
  mimeType: string,
  onProgress?: (pct: number) => void,
): Promise<EvidenceDocument> {
  const pre = await presignPortalEvidence({
    setId,
    fileName: file.name,
    mimeType,
    sizeBytes: file.size,
  });
  await uploadFileWithProgress(pre.uploadUrl, file, onProgress);
  return commitPortalEvidence(pre.evidence.id);
}

export async function linkPortalEvidence(body: {
  setId: string;
  evidenceId: string;
  targets: { metricId: string; entityId: string; year: number }[];
  locator?: KpiEvidenceLocator;
}): Promise<{ linked: number; valueIds: string[] }> {
  return api("/portal/kpi/values/evidence-links", { method: "POST", body });
}

export type PortalLinkedEvidence = KpiLinkedEvidence & { downloadable: boolean };

export async function listPortalValueEvidence(
  valueId: string,
  setId: string,
): Promise<PortalLinkedEvidence[]> {
  const res = await api<{ evidence: PortalLinkedEvidence[] }>(
    `/portal/kpi/values/${valueId}/evidence?setId=${encodeURIComponent(setId)}`,
  );
  return res.evidence;
}

export function portalEvidenceDownloadUrl(evidenceId: string): string {
  return `/api/v1/portal/kpi/evidence/${evidenceId}/download`;
}

// ─── İçe aktarma (ImportApi — portal uygulaması) ────────────────────────────
interface ErrorBody {
  error?: string;
  message?: string;
  issues?: unknown;
  /** 2026-10-08 — `scan_pending` / `scan_blocked` gibi alt kodlar (KPI_INVALID_STATE) */
  details?: unknown;
}

async function request<T>(
  path: string,
  opts: { method?: "GET" | "POST" | "PUT" | "DELETE"; body?: unknown } = {},
): Promise<T> {
  const res = await apiRaw<T | ErrorBody>(path, opts);
  if (res.ok) return res.body as T;
  const b: ErrorBody = (res.body as ErrorBody | null) ?? {};
  const issues: ZodIssueLike[] = Array.isArray(b.issues)
    ? (b.issues as { path?: unknown; message?: unknown }[]).map((i) => ({
        path: Array.isArray(i.path)
          ? i.path.filter(
              (p): p is string | number => typeof p === "string" || typeof p === "number",
            )
          : [],
        message: typeof i.message === "string" ? i.message : "geçersiz değer",
      }))
    : [];
  const message =
    b.message ??
    (issues.length > 0 ? "Gönderilen veri doğrulanamadı" : `HTTP ${res.status.toString()}`);
  throw new KpiImportRequestError(res.status, b.error ?? "unknown", message, issues, b.details);
}

/**
 * 2026-10-08 — portal kanıt özeti (`GET /portal/kpi/evidence/:id`): yükleme adımı iş açmadan önce
 * `scanStatus`'u yoklar (iş yalnız `clean` dosyada açılır). İndirme ucu ayrıdır (`.../download`).
 */
export async function getPortalEvidence(
  evidenceId: string,
): Promise<
  Pick<
    EvidenceDocument,
    "id" | "fileName" | "mimeType" | "sizeBytes" | "scanStatus" | "sourceType" | "uploadedAt"
  > & { purgedAt: string | null }
> {
  const res = await api<{
    evidence: Pick<
      EvidenceDocument,
      "id" | "fileName" | "mimeType" | "sizeBytes" | "scanStatus" | "sourceType" | "uploadedAt"
    > & { purgedAt: string | null };
  }>(`/portal/kpi/evidence/${evidenceId}`);
  return res.evidence;
}

export const PORTAL_IMPORT_API: ImportApi = {
  uploadFile: (ctx, file, kind, onProgress) =>
    uploadPortalEvidence(ctx.setId, file, kpiImportMime(kind), onProgress),
  createJob: async (setId, evidenceId) => {
    const res = await request<{ job: KpiImportJobDto }>(`/portal/kpi/sets/${setId}/import`, {
      method: "POST",
      body: { evidenceId },
    });
    return res.job;
  },
  getEvidence: getPortalEvidence,
  getJob: async (jobId) => {
    const res = await api<{ job: KpiImportJobDto }>(`/portal/kpi/import-jobs/${jobId}`);
    return res.job;
  },
  listJobs: async (setId) => {
    const res = await api<{ jobs: KpiImportJobDto[] }>(`/portal/kpi/sets/${setId}/import-jobs`);
    return res.jobs;
  },
  saveMapping: async (jobId, mapping, columns, defaults) => {
    const res = await request<{ job: KpiImportJobDto }>(
      `/portal/kpi/import-jobs/${jobId}/mapping`,
      {
        method: "PUT",
        body: { mapping, ...(columns ? { columns } : {}), ...defaultsBody(defaults) },
      },
    );
    return res.job;
  },
  deleteJob: async (jobId) => {
    await request(`/portal/kpi/import-jobs/${jobId}`, { method: "DELETE" });
  },
  preview: (jobId, mapping, defaults) =>
    request<KpiImportPreview>(`/portal/kpi/import-jobs/${jobId}/preview`, {
      method: "POST",
      body: { mapping, ...defaultsBody(defaults) },
    }),
  commit: (jobId, mapping, conflictPolicy, defaults) =>
    request<KpiImportCommitResult>(`/portal/kpi/import-jobs/${jobId}/commit`, {
      method: "POST",
      body: { mapping, conflictPolicy, ...defaultsBody(defaults) },
    }),
  retry: async (jobId, opts = {}) => {
    const res = await request<{ job: KpiImportJobDto }>(`/portal/kpi/import-jobs/${jobId}/retry`, {
      method: "POST",
      body: opts.skipMapping ? { skipMapping: true } : {},
    });
    return res.job;
  },
  loadTargets: async (setId) => {
    const set = await getPortalKpiSet(setId);
    return { setIndicators: set.indicators, catalog: [] };
  },
};

export function isPortalForbidden(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    (err.code === "KPI_CLIENT_FORBIDDEN" || err.code === "KPI_CLIENT_FORBIDDEN_ACTION")
  );
}
